import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@/components/atoms/spinner';
import { HttpService, SKIP_AUTO_LOGIN_ONCE_KEY } from '@/features/api/http.service';
import type { SessionContextValue } from '@/features/auth/context/SessionContext';
import { useSession } from '@/features/auth/context/SessionContext';
import { SessionProvider } from '@/features/auth/context/SessionProvider';
import { useAuthTracking } from '@/hooks/useAuthTracking';
import { notifyError } from '@/libs/bugsnag';
import ConnectionErrorPage from '@/pages/ConnectionErrorPage';
import SignedOutPage from '@/pages/SignedOutPage';

// Internal component that handles content rendering and auth tracking
function LoginGuardContent({ children }: { children: React.ReactNode }) {
  const { status } = useSession();
  useAuthTracking();

  if (status === 'signed-in') return <>{children}</>;
  if (status === 'signed-out') return <SignedOutPage returnUrl={window.location.href} />;
  if (status === 'error') return <ConnectionErrorPage />;
  if (status === 'in-progress')
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );

  return null;
}

export const LoginGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SessionContextValue>({ status: 'in-progress' });
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (attemptedRef.current) return;
    // Do not probe an OIDC-protected endpoint after explicit sign-out: the
    // provider may silently restore the session before we can show this page.
    let signedOut = false;
    try {
      signedOut = sessionStorage.getItem(SKIP_AUTO_LOGIN_ONCE_KEY) === '1';
    } catch (error) {
      notifyError(error, { context: 'auth:skip-auto-login-flag:read' });
    }
    if (signedOut) {
      attemptedRef.current = true;
      setState({ status: 'signed-out' });
      return;
    }
    void (async () => {
      try {
        const me = await HttpService.getSessionMe();
        setState({ status: 'signed-in', user: me });
      } catch (error: unknown) {
        const unauthorized =
          typeof error === 'object' && error !== null && 'status' in error && error.status === 401;
        setState(unauthorized ? { status: 'signed-out' } : { status: 'error' });
      }
    })();
  }, []);

  useEffect(() => {
    if (state.status === 'signed-out' && !attemptedRef.current) {
      attemptedRef.current = true;
      HttpService.getSession(window.location.href);
    }
  }, [state.status]);

  return (
    <SessionProvider value={state}>
      <LoginGuardContent>{children}</LoginGuardContent>
    </SessionProvider>
  );
};
